if (!customElements.get('acmer-newsroom-results')) {
  customElements.define('acmer-newsroom-results', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.grid = this.querySelector('.newsroom-results__grid');
      this.query = this.querySelector('input[name="q"]');
      this.sort = this.querySelector('[data-sort]');
      this.initSort(options);
      this.count = this.querySelector('[data-results-count]');
      this.notice = this.querySelector('[data-results-notice]');
      this.retry = this.querySelector('[data-results-retry]');
      this.pager = this.querySelector('.newsroom-results__pagination');
      this.jump = this.querySelector('[data-page-jump] input');
      this.groups = [...this.querySelectorAll('[data-filter-group]')];
      this.initMobileFilters(options);
      this.cards = new Map();
      this.addCards(this.grid);
      this.nextUrl = this.querySelector('[data-results-next]')?.href || null;
      if (Number(new URL(location.href).searchParams.get('page')) > 1) this.nextUrl = this.dataset.sourceUrl;
      this.querySelector('.newsroom-results__server-pages').hidden = true;
      this.sort.disabled = false;
      this.groups.forEach(group => group.querySelectorAll('input').forEach(input => { input.disabled = false; }));
      this.querySelector('.newsroom-results__filters').addEventListener('change', event => {
        if (event.target.matches('input[type="checkbox"]')) this.update();
      }, options);
      this.readUrl();
      this.query.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(true), 200);
      }, options);
      this.querySelector('form[role="search"]').addEventListener('submit', event => {
        event.preventDefault();
        clearTimeout(this.timer);
        this.update();
      }, options);
      this.sort.addEventListener('change', () => this.update(), options);
      this.querySelector('[data-clear-filters]').addEventListener('click', () => {
        this.groups.forEach(group => group.querySelectorAll('input').forEach(input => { input.checked = false; }));
        this.update();
      }, options);
      this.pager.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || button.disabled) return;
        const page = button.hasAttribute('data-page-previous') ? this.page - 1 : button.hasAttribute('data-page-next') ? this.page + 1 : Number(button.dataset.page);
        if (page) this.goToPage(page);
      }, options);
      this.querySelector('[data-page-jump]').addEventListener('submit', event => {
        event.preventDefault();
        if (this.jump.checkValidity()) this.goToPage(Number(this.jump.value));
      }, options);
      this.retry.addEventListener('click', () => this.loadArticles(), options);
      window.addEventListener('popstate', () => { this.readUrl(); this.render(); }, options);
      this.loadArticles();
    }

    disconnectedCallback() {
      this.closeSort();
      this.closeFilters(false);
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.timer);
    }

    initSort(options) {
      this.sortMenu = this.querySelector('[data-sort-options]');
      this.sortOptions = [...this.sortMenu.querySelectorAll('[data-sort-value]')];
      this.sortLabel = this.querySelector('[data-sort-label]');
      this.sort.addEventListener('click', () => {
        if (this.sortMenu.hidden) this.openSort();
        else this.closeSort();
      }, options);
      this.sort.addEventListener('keydown', event => {
        const open = !this.sortMenu.hidden;
        if (event.key === 'Escape' || event.key === 'Tab') {
          if (open && event.key === 'Escape') event.preventDefault();
          this.closeSort();
          return;
        }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (!open) this.openSort();
          let index = this.sortActiveIndex;
          if (event.key === 'Home') index = 0;
          else if (event.key === 'End') index = this.sortOptions.length - 1;
          else if (open) index += event.key === 'ArrowDown' ? 1 : -1;
          this.setSortActive(index);
        } else if (open && ['Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.selectSort(this.sortOptions[this.sortActiveIndex]);
        } else if (event.key.length === 1 && /\S/.test(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
          const index = this.sortOptions.findIndex(option => option.textContent.trim().toLowerCase().startsWith(event.key.toLowerCase()));
          if (index >= 0) {
            event.preventDefault();
            if (!open) this.openSort();
            this.setSortActive(index);
          }
        }
      }, options);
      this.sortMenu.addEventListener('pointerdown', event => event.preventDefault(), options);
      this.sortMenu.addEventListener('click', event => {
        const option = event.target.closest('[data-sort-value]');
        if (option) this.selectSort(option);
      }, options);
      document.addEventListener('pointerdown', event => {
        if (!this.sort.parentElement.contains(event.target)) this.closeSort();
      }, options);
      this.sort.addEventListener('blur', () => this.closeSort(), options);
    }

    syncSort() {
      this.sortOptions.forEach(option => {
        const selected = option.dataset.sortValue === this.sort.value;
        option.setAttribute('aria-selected', String(selected));
        if (selected) this.sortLabel.textContent = option.textContent;
      });
    }

    openSort() {
      this.sortMenu.hidden = false;
      this.sort.setAttribute('aria-expanded', 'true');
      this.setSortActive(this.sortOptions.findIndex(option => option.dataset.sortValue === this.sort.value));
    }

    setSortActive(index) {
      this.sortActiveIndex = Math.max(0, Math.min(index, this.sortOptions.length - 1));
      this.sortOptions.forEach((option, i) => option.classList.toggle('is-active', i === this.sortActiveIndex));
      this.sort.setAttribute('aria-activedescendant', this.sortOptions[this.sortActiveIndex].id);
    }

    closeSort() {
      if (!this.sortMenu) return;
      this.sortMenu.hidden = true;
      this.sort.setAttribute('aria-expanded', 'false');
      this.sort.removeAttribute('aria-activedescendant');
      this.sortOptions.forEach(option => option.classList.remove('is-active'));
    }

    selectSort(option) {
      const changed = this.sort.value !== option.dataset.sortValue;
      this.sort.value = option.dataset.sortValue;
      this.syncSort();
      this.closeSort();
      this.sort.focus({ preventScroll: true });
      if (changed) this.sort.dispatchEvent(new Event('change', { bubbles: true }));
    }

    initMobileFilters(options) {
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.pageSize = this.mobile.matches ? 4 : 15;
      this.filters = this.querySelector('.newsroom-results__filters');
      this.filterHome = this.filters.parentElement;
      this.filterDialog = this.querySelector('.newsroom-results__filter-dialog');
      this.filterToggle = this.querySelector('[data-filter-toggle]');
      this.filterToggle.hidden = false;
      this.query.placeholder = this.mobile.matches ? 'Search Newsroom' : 'Search…';
      this.filterToggle.addEventListener('click', () => {
        if (!this.mobile.matches || this.filterDialog.open) return;
        this.filterDialog.append(this.filters);
        this.filterDialog.showModal();
        this.filterToggle.setAttribute('aria-expanded', 'true');
        this.scrollStyles = [document.documentElement.style.overflow, document.body.style.overflow];
        document.documentElement.style.overflow = document.body.style.overflow = 'hidden';
        this.querySelector('[data-filter-close]').focus({ preventScroll: true });
      }, options);
      this.querySelector('[data-filter-close]').addEventListener('click', () => this.closeFilters(), options);
      this.filterDialog.addEventListener('cancel', event => { event.preventDefault(); this.closeFilters(); }, options);
      this.filterDialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.filterDialog.querySelectorAll('button:not(:disabled), input:not(:disabled)')].filter(element => element.getClientRects().length);
        const target = event.shiftKey && document.activeElement === focusable[0] ? focusable.at(-1) : !event.shiftKey && document.activeElement === focusable.at(-1) ? focusable[0] : null;
        if (target) { event.preventDefault(); target.focus(); }
      }, options);
      this.filterDialog.addEventListener('close', () => { if (!this.filterDialog.open) this.closeFilters(false); }, options);
      this.filterDialog.addEventListener('click', event => {
        if (event.target !== this.filterDialog) return;
        const bounds = this.filterDialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeFilters();
      }, options);
      this.mobile.addEventListener('change', () => {
        const firstIndex = (this.page - 1) * this.pageSize;
        const focusInFilters = this.filters.contains(document.activeElement);
        this.closeFilters(false);
        this.pageSize = this.mobile.matches ? 4 : 15;
        this.page = Math.floor(firstIndex / this.pageSize) + 1;
        this.query.placeholder = this.mobile.matches ? 'Search Newsroom' : 'Search…';
        this.render();
        this.writeUrl(true);
        if (focusInFilters) (this.mobile.matches ? this.filterToggle : this.query).focus({ preventScroll: true });
      }, options);
    }

    closeFilters(restoreFocus = true) {
      if (!this.filterDialog) return;
      const wasOpen = this.filterDialog.open;
      if (wasOpen) this.filterDialog.close();
      if (this.filters.parentElement === this.filterDialog) this.filterHome.prepend(this.filters);
      this.filterToggle.setAttribute('aria-expanded', 'false');
      if (this.scrollStyles) {
        [document.documentElement.style.overflow, document.body.style.overflow] = this.scrollStyles;
        this.scrollStyles = null;
      }
      if (restoreFocus && wasOpen) this.filterToggle.focus({ preventScroll: true });
    }

    addCards(container) {
      container.querySelectorAll('[data-result-card]').forEach(element => {
        if (this.cards.has(element.dataset.articleId)) return;
        let tags = [];
        try { tags = JSON.parse(element.dataset.tags); } catch { /* Keep malformed tags from breaking other articles. */ }
        if (!Array.isArray(tags)) tags = [];
        tags = tags.filter(tag => typeof tag === 'string');
        // Keep article metadata searchable without using it to build the filter menu.
        const categories = this.splitOptions(element.dataset.category || '');
        let categoryOptions;
        try { categoryOptions = JSON.parse(element.dataset.categoryOptions); } catch { /* Ignore empty or invalid search metadata. */ }
        categoryOptions = this.splitOptions(categoryOptions);
        const searchable = [element.dataset.search || '', ...categories, ...categoryOptions];
        this.cards.set(element.dataset.articleId, {
          element,
          title: element.dataset.title,
          date: Number(element.dataset.date) || 0,
          text: this.normalize(searchable.join(' ')),
          tags: tags.map(tag => this.normalize(tag))
        });
      });
    }

    normalize(value) {
      return value.trim().toLocaleLowerCase();
    }

    splitOptions(value) {
      const values = Array.isArray(value) ? value : [value];
      const unique = new Map();
      values.filter(item => typeof item === 'string').forEach(item => {
        item.split(/[,，]/).map(option => option.trim()).filter(Boolean).forEach(option => {
          const key = this.normalize(option);
          if (!unique.has(key)) unique.set(key, option);
        });
      });
      return [...unique.values()];
    }

    readUrl() {
      const params = new URL(location.href).searchParams;
      this.query.value = params.get('q') || '';
      const sort = params.get('sort') || 'recommended';
      this.sort.value = ['recommended', 'newest'].includes(sort) ? sort : 'recommended';
      this.syncSort();
      this.closeSort();
      this.page = Math.max(1, Math.floor(Number(params.get('result_page')) || 1));
      this.groups.forEach(group => {
        const selected = params.getAll(`filter.${group.dataset.filterGroup}`).map(value => this.normalize(value));
        group.querySelectorAll('input').forEach(input => { input.checked = selected.includes(this.normalize(input.value)); });
      });
    }

    writeUrl(replace = false) {
      const url = new URL(location.href);
      const sourceUrl = new URL(this.dataset.sourceUrl, location.href);
      if (sourceUrl.searchParams.has('view')) url.searchParams.set('view', 'newsroom-results');
      else url.searchParams.delete('view');
      url.searchParams.delete('page');
      const query = this.query.value.trim();
      if (query) url.searchParams.set('q', query); else url.searchParams.delete('q');
      if (this.sort.value !== 'recommended') url.searchParams.set('sort', this.sort.value); else url.searchParams.delete('sort');
      if (this.page > 1) url.searchParams.set('result_page', this.page); else url.searchParams.delete('result_page');
      this.groups.forEach(group => {
        const key = `filter.${group.dataset.filterGroup}`;
        url.searchParams.delete(key);
        group.querySelectorAll('input:checked').forEach(input => url.searchParams.append(key, input.value));
      });
      if (url.href !== location.href) history[replace ? 'replaceState' : 'pushState'](null, '', url);
    }

    update(replace = false) {
      this.page = 1;
      this.render();
      this.writeUrl(replace);
    }

    goToPage(page) {
      this.page = Math.max(1, Math.min(page, this.pageCount));
      this.render();
      this.writeUrl();
      this.count.tabIndex = -1;
      this.count.focus({ preventScroll: true });
      this.count.scrollIntoView({ block: 'start' });
    }

    render() {
      const terms = this.query.value.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      const groups = this.groups.map(group =>
        [...group.querySelectorAll('input:checked')].map(input => this.normalize(input.value))
      ).filter(values => values.length);
      // Match any selected tag within a block, and every selected block.
      const matches = [...this.cards.values()].filter(card => terms.every(term => card.text.includes(term)) &&
        groups.every(values => values.some(value => card.tags.includes(value))));
      matches.sort((a, b) => b.date - a.date);
      this.pageCount = Math.max(1, Math.ceil(matches.length / this.pageSize));
      const displayPage = Math.min(this.page, this.pageCount);
      if (!this.loading && !this.failed) this.page = displayPage;
      this.grid.replaceChildren(...matches.slice((displayPage - 1) * this.pageSize, displayPage * this.pageSize).map(card => card.element));
      this.count.textContent = this.loading ? 'Searching all newsroom articles…' : `Showing ${matches.length} results${this.failed ? ' loaded so far' : ''}`;
      this.notice.hidden = !this.failed && (this.loading || matches.length > 0);
      this.notice.textContent = this.failed ? 'Some articles could not be loaded. Retry to see the complete results.' : 'No articles found. Try another search or change your filters.';
      this.retry.hidden = !this.failed;
      this.querySelector('[data-clear-filters]').hidden = groups.length === 0;
      this.pager.hidden = matches.length <= this.pageSize;
      this.querySelector('[data-page-total]').textContent = `of ${this.pageCount}`;
      this.querySelector('[data-page-previous]').disabled = displayPage <= 1;
      this.querySelector('[data-page-next]').disabled = displayPage >= this.pageCount;
      const numbers = this.querySelector('[data-page-numbers]');
      numbers.replaceChildren();
      const start = Math.max(1, Math.min(displayPage - 2, this.pageCount - 4));
      for (let page = start; page <= Math.min(this.pageCount, start + 4); page += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.page = page;
        button.textContent = page;
        button.setAttribute('aria-label', `Page ${page}`);
        if (page === displayPage) button.setAttribute('aria-current', 'page');
        numbers.append(button);
      }
      this.jump.max = this.pageCount;
      this.jump.value = displayPage;
    }

    async loadArticles() {
      if (this.loading) return;
      this.loading = true;
      this.failed = false;
      this.grid.setAttribute('aria-busy', 'true');
      this.render();
      const visited = new Set();
      try {
        while (this.nextUrl) {
          const url = new URL(this.nextUrl, location.href);
          const sourceUrl = new URL(this.dataset.sourceUrl, location.href);
          if (sourceUrl.searchParams.has('view')) url.searchParams.set('view', 'newsroom-results');
          else url.searchParams.delete('view');
          url.searchParams.set('section_id', this.dataset.sectionId);
          if (visited.has(url.href)) throw new Error('Pagination did not advance');
          visited.add(url.href);
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Unable to load articles');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = document.querySelector('acmer-newsroom-results');
          if (!root?.querySelector('.newsroom-results__grid')) throw new Error('Missing article results');
          this.addCards(root);
          this.nextUrl = root.querySelector('[data-results-next]')?.getAttribute('href') || null;
        }
      } catch (error) {
        if (error.name !== 'AbortError') this.failed = true;
      } finally {
        this.loading = false;
        this.grid.removeAttribute('aria-busy');
        this.render();
      }
    }
  });
}
